<template>
  <div class="p-4">

    <div v-if="!loaded" class="text-center py-4 text-muted">
      <i class="ri-loader-4-line"></i> Cargando...
    </div>

    <div v-else>

      <!-- Selectores: mes / año recargan del backend; empresa filtra en cliente (muestra/oculta calendarios). -->
      <div class="flex items-center mb-4 flex-wrap -mx-2">
        <div class="mr-3 mb-2" style="width: 140px">
          <label class="label-form block">Mes</label>
          <wz-select v-model="month" :items="monthOptions" @change="loadData" />
        </div>
        <div class="mr-3 mb-2" style="width: 110px">
          <label class="label-form block">Año</label>
          <wz-select v-model="year" :items="yearOptions" @change="loadData" />
        </div>
        <div v-if="companies.length > 1" class="mr-3 mb-2" style="width: 220px">
          <label class="label-form block">Empresa</label>
          <wz-select
            :items="companies"
            :value="companyFilter"
            defaultOption="Todas las empresas"
            @change="onCompanyFilter"
          />
        </div>
      </div>

      <!-- Sin contratos vigentes → sin calendarios -->
      <div v-if="visibleBlocks.length === 0" class="text-center text-muted py-4">
        No hay relaciones laborales vigentes para mostrar en este período.
      </div>

      <!-- Un mini-calendario por relación laboral (empresa) -->
      <div v-for="block in visibleBlocks" :key="block.contractId || block.companyId" class="mc-block mb-4">
        <div class="mc-header">
          <i class="ri-building-line mr-1"></i>{{ block.companyName || 'Empresa' }}
        </div>

        <div class="mc-scroll">
          <div class="mc-grid" :style="gridStyle">
            <!-- Fila de encabezado: número de día + inicial del día de semana -->
            <div
              v-for="day in block.days"
              :key="'h-' + day.date"
              class="mc-header-day"
              :class="{ weekend: isWeekend(day.date), today: isToday(day.date) }"
            >
              <div class="day-number">{{ dayNumber(day.date) }}</div>
              <div class="day-letter">{{ dayLetter(day.date) }}</div>
            </div>

            <!-- Fila de celdas -->
            <div
              v-for="day in block.days"
              :key="'c-' + day.date"
              class="mc-day-cell"
              :class="{ weekend: isWeekend(day.date), today: isToday(day.date) }"
              @click="openCell(day, block)"
            >
              <div
                v-if="day.status !== 'FUERA_DE_PERIODO'"
                class="mc-badge"
                :class="{
                  'auto-detected': day.isAutoDetected,
                  'has-tardiness': day.tardinessMinutes > 0,
                  'no-record': day.status === 'SIN_REGISTRO',
                }"
                :style="{ backgroundColor: statusColor(day.status) }"
                :title="cellTooltip(day)"
              >
                {{ statusLetter(day.status) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Leyenda (mismo esquema que la grilla principal) -->
      <div v-if="visibleBlocks.length" class="mc-legend flex flex-wrap items-center mt-3 -mx-2">
        <div v-for="s in statusList" :key="s.key" class="legend-item mr-3 mb-1">
          <span class="legend-dot" :style="{ backgroundColor: s.color }"></span>
          <span class="text-sm ml-1">{{ s.label }}</span>
        </div>
        <div class="legend-item mr-3 mb-1">
          <span class="legend-dot no-record-dot"></span>
          <span class="text-sm ml-1">Sin registro</span>
        </div>
        <div class="legend-item mr-3 mb-1">
          <span class="legend-dot out-of-range-dot"></span>
          <span class="text-sm ml-1">Fuera de período</span>
        </div>
        <div class="legend-item mr-3 mb-1">
          <span class="legend-dot tardiness-dot"></span>
          <span class="text-sm ml-1">Con tardanza</span>
        </div>
        <div class="legend-item mb-1">
          <span class="legend-dot auto-dot"></span>
          <span class="text-sm ml-1">Auto-detectado</span>
        </div>
      </div>

    </div>

    <!-- Detalle de celda (solo lectura: para editar está la grilla principal de Asistencia) -->
    <wz-modal
      v-if="selectedCell"
      :value="modalVisible"
      :title="`Asistencia — ${selectedCell.companyName || ''}`"
      size="sm"
      @close="modalVisible = false"
    >
      <table class="wz-table detail-table">
        <tbody>
          <tr><th>Fecha</th><td><span v-local-date="selectedCell.day.date"></span></td></tr>
          <tr>
            <th>Estado</th>
            <td>
              <span
                class="att-badge"
                :style="{ backgroundColor: statusColor(selectedCell.day.status), color: '#fff' }"
              >{{ statusLabel(selectedCell.day.status) }}</span>
            </td>
          </tr>
          <tr><th>Entrada</th><td>{{ selectedCell.day.entryTime || '—' }}</td></tr>
          <tr><th>Salida</th><td>{{ selectedCell.day.exitTime || '—' }}</td></tr>
          <tr><th>Tardanza (min)</th><td>{{ selectedCell.day.tardinessMinutes || 0 }}</td></tr>
          <tr><th>Horas extra</th><td>{{ selectedCell.day.overtimeHours || 0 }}</td></tr>
          <tr><th>Horas trabajadas</th><td>{{ selectedCell.day.hoursWorked || 0 }}</td></tr>
          <tr><th>Observaciones</th><td>{{ selectedCell.day.observations || '—' }}</td></tr>
        </tbody>
      </table>
      <div class="flex justify-end mt-3">
        <wz-button variant="light" @click="modalVisible = false">Cerrar</wz-button>
      </div>
    </wz-modal>

  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { AttendanceService } from '@/core/services'
import { Constants } from '@/core/utils'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]
const DAY_LETTERS = ['D', 'L', 'M', 'X', 'J', 'V', 'S']

const SIN_REGISTRO     = 'SIN_REGISTRO'
const FUERA_DE_PERIODO = 'FUERA_DE_PERIODO'

export default {
  name: 'AttendanceTab',

  props: {
    employeeId:       { type: Number, default: null },
    currentContracts: { type: Array,  default: () => [] },
  },

  data() {
    const now = new Date()
    return {
      loaded:       false,
      year:         now.getFullYear(),
      month:        now.getMonth() + 1,
      companyFilter: null, // null = "Todas las empresas" (filtro cliente, no cambia lo que se pide al backend)
      blocks:       [],
      statusList:   Object.values(Constants.AttendanceStatus),
      modalVisible: false,
      selectedCell: null,
    }
  },

  computed: {
    monthOptions() {
      return MONTH_NAMES.map((label, idx) => ({ key: idx + 1, label }))
    },
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 4 }, (_, i) => ({ key: current - 1 + i, label: String(current - 1 + i) }))
    },
    // Empresas donde el colaborador tiene contrato vigente (para el filtro de calendarios).
    companies() {
      const byId = {}
      ;(this.currentContracts || []).forEach(c => {
        const co = c.company
        if (co && co.id != null && !byId[co.id]) {
          byId[co.id] = { key: co.id, label: co.name || co.businessName || `Empresa ${co.id}` }
        }
      })
      return Object.values(byId)
    },
    // Calendarios visibles según el filtro de empresa (client-side: se piden todos y se ocultan aquí).
    visibleBlocks() {
      if (this.companyFilter == null) return this.blocks
      return this.blocks.filter(b => String(b.companyId) === String(this.companyFilter))
    },
    daysInMonth() {
      return new Date(this.year, this.month, 0).getDate()
    },
    gridStyle() {
      return `grid-template-columns: repeat(${this.daysInMonth}, 32px)`
    },
  },

  methods: {
    onCompanyFilter(id) {
      this.companyFilter = (id === null || id === '' || id === 'null' || id === undefined) ? null : id
    },

    async loadData() {
      if (!this.employeeId) { this.loaded = true; return }
      try {
        this.loaded = false
        // Se pide el mes completo (todas las empresas); el filtro de empresa actúa en cliente.
        const resp = await AttendanceService.getMonthRecords(this.employeeId, this.year, this.month)
        this.blocks = resp.payload || []
      } catch (e) {
        Alert.error('Error al cargar la asistencia del colaborador')
      } finally {
        this.loaded = true
      }
    },

    openCell(day, block) {
      if (day.status === FUERA_DE_PERIODO) return // celda vacía: no hay detalle que mostrar
      this.selectedCell = { day, companyName: block.companyName }
      this.modalVisible = true
    },

    // ── Helpers de fecha (day.date es 'yyyy-MM-dd') ──
    parseDate(dateStr) {
      return new Date(dateStr + 'T00:00:00')
    },
    dayNumber(dateStr) {
      return this.parseDate(dateStr).getDate()
    },
    dayLetter(dateStr) {
      return DAY_LETTERS[this.parseDate(dateStr).getDay()]
    },
    isWeekend(dateStr) {
      const dow = this.parseDate(dateStr).getDay()
      return dow === 0 || dow === 6
    },
    isToday(dateStr) {
      const d = this.parseDate(dateStr)
      const now = new Date()
      return now.getFullYear() === d.getFullYear() &&
             now.getMonth()    === d.getMonth() &&
             now.getDate()     === d.getDate()
    },

    // ── Estado → color / letra / etiqueta (incluye los estados sintéticos del tab) ──
    statusColor(key) {
      if (key === SIN_REGISTRO) return '#e9ecef'
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === key)
      return found ? found.color : '#e9ecef'
    },
    statusLetter(key) {
      if (key === SIN_REGISTRO) return '?'
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === key)
      return found ? found.letter : ''
    },
    statusLabel(key) {
      if (key === SIN_REGISTRO) return 'Sin registro'
      if (key === FUERA_DE_PERIODO) return 'Fuera de período'
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === key)
      return found ? found.label : key
    },

    cellTooltip(day) {
      let tip = this.statusLabel(day.status)
      if (day.entryTime) tip += ` | Entrada: ${day.entryTime}`
      if (day.exitTime)  tip += ` | Salida: ${day.exitTime}`
      if (day.tardinessMinutes > 0) tip += ` | Tardanza: ${day.tardinessMinutes} min`
      return tip
    },
  },

  mounted() {
    this.loadData()
  },
}
</script>

<style scoped lang="stylus">
.mc-block
  border 1px solid #e9ecef
  border-radius 6px
  overflow hidden

.mc-header
  background #f8f9fa
  border-bottom 1px solid #e9ecef
  padding 8px 12px
  font-weight 600
  font-size 14px
  color #212529

.mc-scroll
  overflow-x auto
  padding 10px 12px

.mc-grid
  display grid
  gap 2px
  min-width max-content

.mc-header-day
  text-align center
  font-size 10px
  color #6c757d
  line-height 1.1
  padding-bottom 2px

  .day-number
    font-weight 700
    color #495057
  &.weekend
    color #adb5bd
  &.today .day-number
    color #007bff

.mc-day-cell
  width 32px
  height 32px
  display flex
  align-items center
  justify-content center
  border-radius 4px
  cursor pointer

  &.weekend
    background #f8f9fa
  &.today
    background #e8f4fd

.mc-badge
  width 26px
  height 26px
  border-radius 4px
  display flex
  align-items center
  justify-content center
  font-size 9px
  font-weight 700
  color #fff
  user-select none
  transition box-shadow 0.1s

  &.no-record
    color #6c757d

  &.auto-detected
    opacity 0.65
    border 1px dashed rgba(0,0,0,0.25)

  &.has-tardiness
    box-shadow 0 2px 0 0 #ffc107

.mc-day-cell:hover .mc-badge
  box-shadow 0 0 0 2px rgba(0,0,0,0.2)

.mc-legend
  .legend-item
    display flex
    align-items center

  .legend-dot
    width 14px
    height 14px
    border-radius 3px
    display inline-block
    flex-shrink 0

  .no-record-dot
    background #e9ecef

  .out-of-range-dot
    background #fff
    border 1px solid #dee2e6

  .tardiness-dot
    background #ffc107
    height 4px
    border-radius 2px

  .auto-dot
    background #adb5bd
    opacity 0.65
    border 1px dashed rgba(0,0,0,0.25)

.att-badge
  display inline-block
  padding 2px 8px
  border-radius 12px
  font-size 11px
  font-weight 700

.detail-table
  width 100%
  th
    text-align left
    color #6c757d
    font-weight 600
    white-space nowrap
    padding-right 12px
    width 40%
</style>
